import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import CreateAccountForm from './components/CreateAccountForm';
import EditAccountModal from './components/EditAccountModal';
import AccountGroup from './components/AccountGroup';
import PortfolioSummary from './components/PortfolioSummary';
import { accountService } from '../../services/accountService';
import { useAuth } from '../../contexts/AuthContext';
import DeleteAccountModal from './components/DeleteAccountModal';
import { useSubscription } from '../../contexts/SubscriptionContext';
import UpgradeModal from '../../components/ui/UpgradeModal';


const AccountsOverview = () => {
  const navigate = useNavigate();
  const { user, userProfile, loading: authLoading } = useAuth();
  const { canUse } = useSubscription();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeInfo, setUpgradeInfo] = useState({ feature: '', limit: 0, current: 0 });

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/dashboard' },
    { label: 'Accounts Overview', path: '/accounts-overview' }
  ];

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user) {
      loadAccounts();
    }
  }, [user]);

  const loadAccounts = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await accountService?.getAll();
      setAccounts(data);
    } catch (err) {
      console.error('Error loading accounts:', err);
      setError(err?.message || 'Failed to load accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAccount = async () => {
    // Check account limit
    const { allowed, limit, current } = await canUse('accounts', accounts?.length || 0);
    
    if (!allowed) {
      setUpgradeInfo({ feature: 'accounts', limit, current });
      setShowUpgradeModal(true);
      return;
    }

    setShowCreateForm(true);
  };

  const handleSubmitAccount = async (accountData) => {
    try {
      await accountService?.create(accountData);
      await loadAccounts();
      setShowCreateForm(false);
    } catch (err) {
      console.error('Error creating account:', err);
      throw err;
    }
  };

  const handleEditAccount = (account) => {
    setEditingAccount(account);
    setShowEditModal(true);
  };

  const handleUpdateAccount = async (updates) => {
    try {
      await accountService?.update(editingAccount?.id, updates);
      await loadAccounts();
      setShowEditModal(false);
      setEditingAccount(null);
    } catch (err) {
      console.error('Error updating account:', err);
      throw err;
    }
  };

  const handleDeleteAccount = (account) => {
    setDeletingAccount(account);
    setShowDeleteModal(true);
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setDeletingAccount(null);
  };

  const handleConfirmDelete = async () => {
    setDeleteLoading(true);
    try {
      await accountService?.delete(deletingAccount?.id);
      await loadAccounts();
      setShowDeleteModal(false);
      setDeletingAccount(null);
    } catch (err) {
      console.error('Error deleting account:', err);
    } finally {
      setDeleteLoading(false);
    }
  };

  const assetAccounts = accounts?.filter(acc => acc?.group === 'Assets')?.sort((a, b) => {
    // Sort by type, then by name
    const typeOrder = ['Chequing', 'Savings', 'Investment'];
    const typeCompare = typeOrder?.indexOf(a?.type) - typeOrder?.indexOf(b?.type);
    return typeCompare !== 0 ? typeCompare : a?.name?.localeCompare(b?.name);
  });
  
  const liabilityAccounts = accounts?.filter(acc => acc?.group === 'Liabilities')?.sort((a, b) => {
    // Sort by type, then by name
    const typeOrder = ['Credit Card', 'Line of Credit', 'Loan', 'Collections'];
    const typeCompare = typeOrder?.indexOf(a?.type) - typeOrder?.indexOf(b?.type);
    return typeCompare !== 0 ? typeCompare : a?.name?.localeCompare(b?.name);
  });
  
  const cashAccounts = accounts?.filter(acc => acc?.group === 'Cash')?.sort((a, b) => 
    a?.name?.localeCompare(b?.name)
  );

  const totalAssets = assetAccounts?.reduce((sum, acc) => sum + acc?.balance, 0);
  const totalLiabilities = liabilityAccounts?.reduce((sum, acc) => sum + Math.abs(acc?.balance), 0);
  const totalCash = cashAccounts?.reduce((sum, acc) => sum + acc?.balance, 0);
  const netWorth = totalAssets + totalCash - totalLiabilities;

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-16"></div>
          <p className="text-muted-foreground font-caption">Loading accounts...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Accounts Overview - Rovyn Budget</title>
        <meta name="description" content="Manage your complete financial portfolio with organized account listings and streamlined account creation" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="pt-16">
          <div className="mx-auto px-16 md:px-24 py-24 md:py-32 max-w-[1400px]">
            <div className="mb-24 md:mb-28 lg:mb-32">
              <Breadcrumbs items={breadcrumbItems} />
            </div>

            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-16 mb-24 md:mb-28 lg:mb-32 mt-24">
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-8">
                  Accounts Overview
                </h1>
                <p className="text-sm md:text-base text-muted-foreground font-caption">
                  Manage your financial accounts and track balances
                </p>
              </div>
              <Button
                onClick={handleCreateAccount}
                iconName="Plus"
                iconPosition="left"
              >
                Add Account
              </Button>
            </div>

            {error && (
              <div className="bg-error/10 border border-error/20 rounded-lg p-16 mb-24">
                <p className="text-sm text-error font-caption">{error}</p>
              </div>
            )}

            {!showCreateForm && (
              <>
                <PortfolioSummary
                  totalAssets={totalAssets}
                  totalLiabilities={totalLiabilities}
                  totalCash={totalCash}
                  netWorth={netWorth}
                />

                <AccountGroup
                  title="Asset Accounts"
                  accounts={assetAccounts}
                  onEditAccount={handleEditAccount}
                  onDeleteAccount={handleDeleteAccount}
                />

                {liabilityAccounts?.length > 0 && (
                  <div className="mt-24">
                    <AccountGroup
                      title="Liability Accounts"
                      accounts={liabilityAccounts}
                      onEditAccount={handleEditAccount}
                      onDeleteAccount={handleDeleteAccount}
                    />
                  </div>
                )}

                {cashAccounts?.length > 0 && (
                  <div className="mt-24">
                    <AccountGroup
                      title="Cash on Hand"
                      accounts={cashAccounts}
                      onEditAccount={handleEditAccount}
                      onDeleteAccount={handleDeleteAccount}
                    />
                  </div>
                )}
              </>
            )}

            {accounts?.length === 0 && (
              <div className="bg-card rounded-lg p-40 md:p-48 text-center shadow-elevation-2">
                <div className="flex items-center justify-center w-20 h-20 bg-primary/10 rounded-full mx-auto mb-20">
                  <Icon name="Wallet" size={40} color="var(--color-primary)" strokeWidth={2} />
                </div>
                <h2 className="text-xl md:text-2xl font-heading font-semibold text-card-foreground mb-12">
                  No Accounts Yet
                </h2>
                <p className="text-sm md:text-base text-muted-foreground mb-24 max-w-md mx-auto">
                  Start building your financial portfolio by creating your first account. Track your assets, liabilities, and cash all in one place.
                </p>
                <Button
                  variant="default"
                  size="lg"
                  iconName="Plus"
                  iconPosition="left"
                  iconSize={20}
                  onClick={() => setShowCreateForm(true)}
                >
                  Create Your First Account
                </Button>
              </div>
            )}

            {showUpgradeModal && (
              <UpgradeModal
                isOpen={showUpgradeModal}
                onClose={() => setShowUpgradeModal(false)}
                feature={upgradeInfo?.feature}
                limit={upgradeInfo?.limit}
                current={upgradeInfo?.current}
              />
            )}
          </div>
        </main>

        {showCreateForm && (
          <CreateAccountForm
            onClose={() => setShowCreateForm(false)}
            onSubmit={handleSubmitAccount}
          />
        )}

        {showEditModal && editingAccount && (
          <EditAccountModal
            account={editingAccount}
            onClose={() => {
              setShowEditModal(false);
              setEditingAccount(null);
            }}
            onSave={handleUpdateAccount}
          />
        )}

        <DeleteAccountModal
          isOpen={showDeleteModal}
          onClose={handleCancelDelete}
          onConfirm={handleConfirmDelete}
          account={deletingAccount}
          loading={deleteLoading}
        />
      </div>
    </>
  );
};

export default AccountsOverview;